import { BrowserDelay } from "../../clock/browser-delay.js";
const DEFAULT_STABILITY_MS = 5_000;
/**
 * WebRTC promotion policy (ticket #1825, "N4"): connect over the baseline
 * transport first (owned by the caller, e.g. `HttpTransportSupervisor`),
 * and only ever promote the managed client to `webrtc-direct` after the
 * DataChannel is authenticated (already true once `connect()` resolves —
 * signaling authentication happens before the peer connection is ever
 * allocated, see `WebRtcClientRpcTransport`) *and* stable for
 * `stabilityMs`.
 *
 * Scheduling is deliberately external, matching `HttpTransportSupervisor`:
 * callers invoke `attemptPromotion()` (e.g. once the baseline is connected
 * and idle, or on a network-change signal). This controller never retries
 * on its own and never tears down the baseline while attempting
 * promotion — a failed or unstable WebRTC attempt just leaves the managed
 * client on whatever it already had.
 */
export class WebRtcPromotionController {
    #client;
    #createClient;
    #delay;
    #stabilityMs;
    #active;
    #attempt;
    #closed = false;
    #unsubscribeActive = () => undefined;
    constructor(options) {
        this.#client = options.client;
        this.#createClient = options.createClient;
        this.#delay = options.delay ?? new BrowserDelay();
        this.#stabilityMs = Math.max(0, options.stabilityMs ?? DEFAULT_STABILITY_MS);
    }
    /**
     * Whether a promoted WebRTC client is currently the managed client's
     * active transport. Self-correcting: the promoted client is monitored for
     * the rest of its lifetime (not just during the stability wait), so this
     * reflects reality even after a natural failure the caller never reacted
     * to directly — e.g. `ManagedHomeNodeClient` retiring it on an ICE
     * disconnect, or a supervisor's own LAN-recovery `replace()` closing it.
     */
    get isPromoted() {
        return this.#active !== undefined;
    }
    /**
     * Attempts one promotion. Returns `true` if the managed client is now
     * running over `webrtc-direct`, `false` otherwise (already promoted,
     * closed, connection failed, or the connection was not stable within
     * `stabilityMs`).
     */
    attemptPromotion(signal) {
        if (this.#closed || this.#active !== undefined)
            return Promise.resolve(this.isPromoted);
        if (this.#attempt !== undefined)
            return this.#attempt;
        const attempt = this.#attemptPromotion(signal).finally(() => {
            if (this.#attempt === attempt)
                this.#attempt = undefined;
        });
        this.#attempt = attempt;
        return attempt;
    }
    /**
     * Restarts ICE on the currently promoted client, if any — the explicit,
     * caller-driven recovery primitive `WebRtcHomeNodeClient.restartIce()`
     * documents for a network-change signal (e.g. the browser's own `online`
     * event after a connectivity gap, or a Wi-Fi/cellular handoff). A no-op
     * when nothing is promoted: the baseline transport (LAN or WAN
     * HTTPS/WSS) owns its own independent reconnect logic and never goes
     * through this controller. Never throws — a failed restart already
     * publishes an offline transition on the promoted client, which the
     * self-correcting subscription below (and `ManagedHomeNodeClient`'s own
     * retire-on-offline wiring) already fall back from.
     */
    async restartIce() {
        if (this.#active === undefined)
            return false;
        try {
            await this.#active.restartIce();
            return true;
        }
        catch {
            return false;
        }
    }
    /** Best-effort demotion back to the baseline, e.g. on an explicit network-change signal. */
    async demote() {
        const active = this.#active;
        if (active === undefined)
            return;
        this.#active = undefined;
        this.#unsubscribeActive();
        this.#unsubscribeActive = () => undefined;
        await closeClient(active);
    }
    async close() {
        this.#closed = true;
        await this.demote();
    }
    async #attemptPromotion(signal) {
        const client = this.#createClient();
        try {
            await client.connect();
        }
        catch {
            await closeClient(client);
            return false;
        }
        if (this.#closed || signal?.aborted === true) {
            await closeClient(client);
            return false;
        }
        const stable = await this.#waitForStability(client, signal);
        if (!stable || this.#closed) {
            await closeClient(client);
            return false;
        }
        this.#active = client;
        this.#unsubscribeActive = client.subscribe((event) => {
            if (event.type === "transport.state-changed" && event.state.status !== "connected" && this.#active === client) {
                this.#active = undefined;
                this.#unsubscribeActive();
                this.#unsubscribeActive = () => undefined;
            }
        });
        await this.#client.replace(client);
        return true;
    }
    async #waitForStability(client, signal) {
        if (client.state.status !== "connected")
            return false;
        let becameUnstable = false;
        const unsubscribe = client.subscribe((event) => {
            if (event.type === "transport.state-changed" && event.state.status !== "connected")
                becameUnstable = true;
        });
        try {
            await this.#delay.wait(this.#stabilityMs);
        }
        finally {
            unsubscribe();
        }
        if (signal?.aborted === true)
            return false;
        return !becameUnstable && client.state.status === "connected";
    }
}
async function closeClient(client) {
    await Promise.resolve(client.close?.()).catch(() => undefined);
}
//# sourceMappingURL=webrtc-promotion-controller.js.map